/**
 * Drifting low-poly ice chunks. Random jagged polygons that drift horizontally and bob vertically.
 */

interface Chunk {
  cx: number
  cy: number
  baseY: number
  radius: number
  vx: number
  bobAmp: number
  bobPhase: number
  verts: { x: number; y: number }[]
  rotation: number
  rotSpeed: number
}

export class IceChunks {
  private chunks: Chunk[] = []
  private w = 0
  private h = 0
  private count: number

  constructor(count: number) {
    this.count = count
  }

  resize(w: number, h: number): void {
    this.w = w
    this.h = h
    if (this.chunks.length === 0) this.spawn()
  }

  setCount(count: number): void {
    this.count = count
    this.spawn()
  }

  private spawn(): void {
    this.chunks = []
    for (let i = 0; i < this.count; i++) {
      const radius = 40 + Math.random() * 90
      const numVerts = 8 + Math.floor(Math.random() * 5)
      const verts: { x: number; y: number }[] = []
      for (let v = 0; v < numVerts; v++) {
        const angle = (v / numVerts) * Math.PI * 2
        const r = radius * (0.65 + Math.random() * 0.55)
        verts.push({ x: Math.cos(angle) * r, y: Math.sin(angle) * r * 0.55 })
      }
      const cy = 100 + Math.random() * (this.h - 300)
      this.chunks.push({
        cx: Math.random() * this.w,
        cy,
        baseY: cy,
        radius,
        vx: (Math.random() > 0.5 ? 1 : -1) * (0.08 + Math.random() * 0.18),
        bobAmp: 2 + Math.random() * 3,
        bobPhase: Math.random() * Math.PI * 2,
        verts,
        rotation: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.0003,
      })
    }
  }

  tick(dt: number): void {
    const step = dt / 16.666
    for (const c of this.chunks) {
      c.cx += c.vx * step
      c.bobPhase += 0.005 * step
      c.cy = c.baseY + Math.sin(c.bobPhase) * c.bobAmp
      c.rotation += c.rotSpeed * dt

      if (c.cx > this.w + c.radius * 2) c.cx = -c.radius * 2
      else if (c.cx < -c.radius * 2) c.cx = this.w + c.radius * 2
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const c of this.chunks) {
      ctx.save()
      ctx.translate(c.cx, c.cy)
      ctx.rotate(c.rotation)

      // Fill — frosted white semi-transparent for light theme
      const g = ctx.createRadialGradient(-c.radius * 0.3, -c.radius * 0.3, 0, 0, 0, c.radius)
      g.addColorStop(0, 'rgba(255, 255, 255, 0.75)')
      g.addColorStop(0.55, 'rgba(220, 238, 252, 0.45)')
      g.addColorStop(1, 'rgba(180, 210, 235, 0.1)')
      ctx.fillStyle = g

      ctx.beginPath()
      ctx.moveTo(c.verts[0].x, c.verts[0].y)
      for (let i = 1; i < c.verts.length; i++) ctx.lineTo(c.verts[i].x, c.verts[i].y)
      ctx.closePath()
      ctx.fill()

      // Soft blue outline
      ctx.strokeStyle = 'rgba(0, 85, 168, 0.18)'
      ctx.lineWidth = 1
      ctx.stroke()

      // Inner highlight edge
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)'
      ctx.lineWidth = 0.6
      ctx.stroke()

      ctx.restore()
    }
  }
}
